'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';

import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  isFilledSelectedState,
  isTwoLineSelectVariant,
  selectRoundedClass,
  selectTriggerVariants,
  type SelectedStateTheme,
  type SelectRounded,
  type SelectTriggerVariantProps,
} from '@/hen-ui/atomics/Select/select.variants';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/hen-ui/shadcn/dropdown-menu';
import { cn } from '@/hen-ui/utils';
import {
  fieldTextSizeClass,
  pickedFieldTextClasses,
  type FieldTextStyleProps,
} from '@/hen-ui/utils/field-text-style';
import {
  buildOneLineTriggerText,
  buildTriggerText,
  computeSelectAllChecked,
  resolveMaxSelectable,
  toggleMultiSelectValue,
  toggleSelectAllValues,
} from './multi-select-state';
import { resolveMultiSelectFieldLabel } from './resolve-field-label';

export type MultiSelectTagDisplay = 'inline' | 'count' | 'list';

/** Button large padding: 24px horizontal, 10px vertical. */
export type MultiSelectSize = 'lg';

export type MultiSelectWidth = 'auto' | 'max-content';

const MULTI_SELECT_SIZE_CLASS: Record<MultiSelectSize, string> = {
  lg: 'px-button-x-lg py-button-y-lg',
};

const MULTI_SELECT_WIDTH_CLASS: Record<MultiSelectWidth, string> = {
  auto: 'w-auto',
  'max-content': 'w-max shrink-0',
};

const multiSelectWidthClass = (
  width: string | undefined,
): string | undefined =>
  width === 'auto' || width === 'max-content'
    ? MULTI_SELECT_WIDTH_CLASS[width]
    : undefined;

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps extends FieldTextStyleProps {
  options?: MultiSelectOption[];
  selectedValues?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  /**
   * `default` and `elevated` keep the field label above the button.
   * `two-line` stacks the label above the selection inside the button.
   * `one-line` puts the label and the selection count on one line inside
   * the button, always as `Label (n)`.
   */
  variant?: SelectTriggerVariantProps['variant'];
  /**
   * Trigger padding. Leave unset for the standard field padding.
   * `lg` uses the Button large padding.
   */
  size?: MultiSelectSize;
  /**
   * Width of the field. Leave unset to stretch across the container.
   * `auto` uses the browser width. `max-content` sizes the field to its
   * label and keeps a flex row from shrinking it.
   */
  width?: MultiSelectWidth;
  /**
   * Field label for every style: shown above the control for `default` and
   * `elevated`, stacked inside the trigger for `two-line`, and written on
   * the same line as the selection count for `one-line`.
   */
  fieldLabel?: string;
  /**
   * Previous catalog name for this text ("Field name"). Read only when
   * `fieldLabel` is empty so saved documents still render.
   */
  label?: string;
  tagDisplay?: MultiSelectTagDisplay;
  maxSelectable?: number;
  selectAllEnabled?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Trigger id. Ties the Field label to the control via `htmlFor` for
   * `default` / `elevated`, and anchors the in-trigger label for `two-line`.
   */
  id?: string;
  /**
   * Ids of elements that describe the trigger (e.g. FormField status /
   * helper text). Forwarded onto the focusable button so screen readers
   * hear them on focus.
   */
  'aria-describedby'?: string;
  /**
   * Fill of the dropdown button once a value is chosen. `default` leaves
   * the button as it is. `primary` uses the brand fill. `secondary` is black.
   * The label is white on either fill. An empty button is unchanged.
   */
  selectedStateTheme?: SelectedStateTheme;
  /**
   * Corner radius of the dropdown button. Unset keeps the style's own
   * corners. A value replaces them, including the extra-large corners on
   * `elevated`.
   */
  rounded?: SelectRounded | '';
  /**
   * While a bound list is refreshing, keep showing the choices already
   * on screen. The binding runtime reads this and does not draw it.
   * Off unless set to true.
   */
  keepOptionsWhileLoading?: boolean;
}

const selectContentClassName = cn(
  'max-h-96 min-w-[8rem]',
  'rounded-sm border border-neutral-200 bg-white text-body shadow-md',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
  'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
  'min-w-[var(--radix-dropdown-menu-trigger-width)]',
);

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(function MultiSelect(
  {
    options = [],
    selectedValues = [],
    onChange,
    placeholder = 'Select...',
    variant = 'default',
    size,
    width,
    fieldLabel,
    label,
    tagDisplay = 'count',
    maxSelectable,
    selectAllEnabled = false,
    disabled = false,
    className,
    id,
    'aria-describedby': ariaDescribedBy,
    placeholderSize,
    valueSize,
    placeholderColor,
    labelColor,
    selectedStateTheme,
    rounded,
  },
  ref,
) {
  const [maxHint, setMaxHint] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const generatedId = React.useId();
  const triggerId = id ?? generatedId;
  const limit = resolveMaxSelectable(maxSelectable);
  const hasValue = selectedValues.length > 0;
  const resolvedFieldLabel = resolveMultiSelectFieldLabel(fieldLabel, label);
  const hasFieldLabel = resolvedFieldLabel.length > 0;
  const isOneLine = variant === 'one-line';
  const isTwoLine = isTwoLineSelectVariant(variant) && hasFieldLabel;
  const filledSelected = isFilledSelectedState(selectedStateTheme, hasValue);
  const showExternalLabel = hasFieldLabel && !isTwoLine && !isOneLine;
  const labelId = isTwoLine ? `${triggerId}-label` : undefined;
  const fieldText = pickedFieldTextClasses(hasValue, false, {
    placeholderSize,
    valueSize,
    placeholderColor,
    labelColor,
  });

  React.useEffect(() => {
    if (
      maxSelectable !== undefined &&
      maxSelectable <= 0 &&
      process.env.NODE_ENV === 'development'
    ) {
      console.warn(
        '[MultiSelect] maxSelectable must be a positive integer; non-positive values are treated as no limit.',
      );
    }
  }, [maxSelectable]);

  React.useEffect(() => {
    if (!maxHint) return;
    const timer = window.setTimeout(() => setMaxHint(false), 2500);
    return () => window.clearTimeout(timer);
  }, [maxHint]);

  const selectedSet = React.useMemo(
    () => new Set(selectedValues),
    [selectedValues],
  );

  const allValues = React.useMemo(() => options.map((o) => o.value), [options]);

  const selectAllChecked = computeSelectAllChecked(allValues, selectedValues);

  const toggleValue = (value: string) => {
    const { next, rejected } = toggleMultiSelectValue(
      selectedValues,
      value,
      limit,
    );
    if (rejected) {
      setMaxHint(true);
      return;
    }
    onChange?.(next);
  };

  const toggleSelectAll = () => {
    onChange?.(toggleSelectAllValues(allValues, selectedValues, limit));
  };

  const triggerText = isOneLine
    ? buildOneLineTriggerText(
        resolvedFieldLabel,
        selectedValues.length,
        placeholder,
      )
    : buildTriggerText(options, selectedValues, tagDisplay, placeholder);

  const showInlineTags =
    !isOneLine && tagDisplay === 'inline' && selectedValues.length > 0;
  const widthClass = multiSelectWidthClass(width);

  const inlineChips = selectedValues.map((value, index) => {
    const chipLabel =
      options.find((option) => option.value === value)?.label ?? value;
    return (
      <span
        key={`${value}-${index}`}
        className={cn(
          'inline-flex shrink-0 max-w-full items-center rounded-sm bg-neutral-100 px-2 py-0.5 text-body',
          valueSize ? fieldTextSizeClass[valueSize] : 'text-b5',
        )}
      >
        <span className='truncate'>{chipLabel}</span>
      </span>
    );
  });

  const selectionNode = showInlineTags ? (
    <span className='flex w-full min-w-0 flex-wrap items-center gap-xxs'>
      {inlineChips}
    </span>
  ) : (
    <span
      className={cn(
        'truncate',
        isTwoLine &&
          cn(
            'w-full font-normal line-clamp-1',
            fieldText.size ?? 'text-sm leading-5',
          ),
        isTwoLine &&
          (filledSelected
            ? 'text-white'
            : hasValue
              ? 'text-body'
              : (fieldText.emptyColor ?? 'text-subtext')),
      )}
    >
      {triggerText}
    </span>
  );

  return (
    // hen-ui-allow-raw-html -- width classes sit on this wrapper so a flex parent sizes the field
    <div className={cn('relative', widthClass ?? 'w-full', className)}>
      {showExternalLabel ? (
        <label
          htmlFor={triggerId}
          className={cn('mb-2 block text-sm font-medium', fieldText.label)}
        >
          {resolvedFieldLabel}
        </label>
      ) : null}
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            ref={ref}
            id={triggerId}
            type='button'
            disabled={disabled}
            aria-haspopup='menu'
            aria-describedby={ariaDescribedBy}
            title={
              maxHint && limit !== undefined
                ? `Maximum ${limit} selections`
                : undefined
            }
            className={cn(
              selectTriggerVariants({
                variant,
                hasValue,
                error: false,
                selectedStateTheme,
              }),
              !isTwoLine && !isOneLine && fieldText.size,
              !isTwoLine && !isOneLine && fieldText.emptyColor,
              showInlineTags &&
                'h-auto py-2 [&>span]:line-clamp-none [&>span]:overflow-visible',
              showInlineTags && (isTwoLine ? 'min-h-14' : 'min-h-11'),
              size ? MULTI_SELECT_SIZE_CLASS[size] : '',
              widthClass,
              selectRoundedClass(rounded),
            )}
            aria-labelledby={labelId}
            data-hen-id={COMPONENT_IDS.MultiSelect}
          >
            {isTwoLine ? (
              // A div, not a span: the shared trigger class clamps every
              // direct child span to one line, which would flatten the
              // label and value into "States...".
              <div className='flex min-w-0 flex-1 flex-col items-start text-left'>
                <span
                  id={labelId}
                  className={cn(
                    'w-full text-l2 font-bold',
                    filledSelected ? 'text-white' : fieldText.label,
                  )}
                >
                  {resolvedFieldLabel}
                </span>
                {selectionNode}
              </div>
            ) : isOneLine ? (
              // A div, not a span: the shared trigger class clamps every
              // direct child span, which would shrink the label onto the chevron.
              <div
                className={cn(
                  'min-w-0 flex-1 truncate text-left font-medium',
                  filledSelected ? 'text-white' : fieldText.label,
                )}
              >
                {triggerText}
              </div>
            ) : (
              <span
                className={cn(
                  'min-w-0 flex-1 text-left',
                  showInlineTags
                    ? '!flex flex-wrap items-center gap-xxs overflow-visible'
                    : 'flex items-center truncate [&>span]:line-clamp-1',
                )}
              >
                {selectionNode}
              </span>
            )}
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-200',
                filledSelected ? 'text-white' : 'text-black',
                isOpen && 'rotate-180',
              )}
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='start'
          sideOffset={4}
          className={selectContentClassName}
        >
          {placeholder.trim() !== '' && (
            <i className='py-xxs px-sm mb-xxs text-b5 text-body'>
              {placeholder}
            </i>
          )}
          {selectAllEnabled && options.length > 0 ? (
            <DropdownMenuCheckboxItem
              checked={selectAllChecked}
              onCheckedChange={() => toggleSelectAll()}
              onSelect={(event: Event) => event.preventDefault()}
              className='font-medium'
            >
              Select all
            </DropdownMenuCheckboxItem>
          ) : null}
          {options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={selectedSet.has(opt.value)}
              onCheckedChange={() => toggleValue(opt.value)}
              onSelect={(event: Event) => event.preventDefault()}
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <span aria-live='polite' className='sr-only'>
        {maxHint && limit !== undefined ? `Maximum ${limit} selections` : ''}
      </span>
    </div>
  );
});

MultiSelect.displayName = 'MultiSelect';
